import { Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentSize } from '../types';

export interface TimelineItem {
  title: string;
  description?: string;
  date?: string;
  icon?: string;
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'info';
}

@Component({
  selector: 'app-timeline',
  imports: [CommonModule],
  template: `
    <div class="relative">
      <div [class]="timelineClasses">
        @for (item of items(); track $index; let isLast = $last) {
          <div class="flex gap-4">
            <div class="flex flex-col items-center">
              <div [class]="getIconClasses(item, $index)">
                @if (item.icon) {
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" [attr.d]="item.icon" />
                  </svg>
                } @else {
                  <div class="w-2 h-2 rounded-full bg-current"></div>
                }
              </div>
              @if (!isLast) {
                <div [class]="lineClasses"></div>
              }
            </div>
            <div class="flex-1 pb-8">
              @if (item.date) {
                <div class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {{ item.date }}
                </div>
              }
              <h3 [class]="titleClasses">{{ item.title }}</h3>
              @if (item.description) {
                <p [class]="descriptionClasses">{{ item.description }}</p>
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: []
})
export class TimelineComponent {
  items = input<TimelineItem[]>([]);
  size = input<ComponentSize>('md');
  variant = input<'default' | 'compact'>('default');

  get timelineClasses(): string {
    return 'space-y-0';
  }

  getIconClasses(item: TimelineItem, index: number): string {
    const sizeMap = {
      xs: 'w-6 h-6',
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
      xl: 'w-14 h-14'
    };

    const colorMap = {
      primary: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
      success: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
      danger: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
      info: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
    };

    return [
      'flex',
      'items-center',
      'justify-center',
      'rounded-full',
      'flex-shrink-0',
      sizeMap[this.size()],
      colorMap[item.color || 'primary']
    ].join(' ');
  }

  get lineClasses(): string {
    return 'w-0.5 h-full bg-gray-200 dark:bg-gray-700';
  }

  get titleClasses(): string {
    const sizeMap = {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl'
    };
    return `font-semibold text-gray-900 dark:text-gray-100 ${sizeMap[this.size()]}`;
  }

  get descriptionClasses(): string {
    return 'text-sm text-gray-600 dark:text-gray-400 mt-1';
  }
}

